import CheckBoxOutlineBlank from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxOutlined from "@material-ui/icons/CheckBoxOutlined";
import { IShiftCalendarUtil } from "Interfaces/Utils/IShiftCalendarUtil";
import { Shift } from "Models/Shifts/Shift";
import { ShiftCalendarItem } from "Models/Shifts/ShiftCalendarItem";
import { TaskRecurrency } from "Models/Tasks/TaskRecurrencies/TaskRecurrency";
import * as React from "react";
import { connect } from "react-redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IAuthenticationState } from "Store/Authentication/AuthenticationActionTypes";
import { IShiftState } from "Store/Shifts/ShiftState";
import { IApplicationState } from "Store/Store";
import i18n from "Translations/i18n";
import styles from "./ShiftCalendar.scss";
import { ShiftCalendarMode } from "./ShiftCalendarMode";

const shiftCalendarUtil = ServiceProvider.get<IShiftCalendarUtil>(Types.IShiftCalendarUtil);

interface IProps {
    id?: string;
    taskRecurrency: TaskRecurrency;
    isReadonly: boolean;
    shifts: Shift[];
    shiftState: IShiftState;
    shiftCalendarMode: ShiftCalendarMode;
    selectedDate?: Date;
    className?: string;
    onRecurrencyChanged(taskRecurrency: TaskRecurrency): void;
}

interface IState {
    shifts: Shift[];
    taskRecurrency: TaskRecurrency;
    selectedDate?: Date;
    shiftCalendarMode: ShiftCalendarMode;
    convertedShifts: ShiftCalendarItem[];
    authenticationState?: IAuthenticationState;
    days: string[];
}

const mapStateToProps = (state: IApplicationState) => ({
    shiftState: state.shiftState
});

export class ShiftCalendar extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);

        this.state = {
            selectedDate: props.selectedDate,
            shiftCalendarMode: props.shiftCalendarMode,
            taskRecurrency: props.taskRecurrency,
            shifts: props.shifts,
            convertedShifts: shiftCalendarUtil.convertShifts(props.shifts, props.shiftCalendarMode, props.selectedDate),
            days: shiftCalendarUtil.getWeekDays(props.shiftCalendarMode)
        };

        this.dayBoxClicked = this.dayBoxClicked.bind(this);
        this.dayBoxIsSelected = this.dayBoxIsSelected.bind(this);
        this.selectAllDays = this.selectAllDays.bind(this);
        this.selectAllTimes = this.selectAllTimes.bind(this);
        this.clearAll = this.clearAll.bind(this);
        this.selectAll = this.selectAll.bind(this);
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const state: Partial<IState> = {};
        if (nextProps.shiftCalendarMode && nextProps.shiftCalendarMode !== prevState.shiftCalendarMode) {
            state.shiftCalendarMode = nextProps.shiftCalendarMode;
            state.days = shiftCalendarUtil.getWeekDays(nextProps.shiftCalendarMode);
            if (nextProps.shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
                state.convertedShifts = shiftCalendarUtil.convertShifts(nextProps.shifts, nextProps.shiftCalendarMode, nextProps.selectedDate);
            }
            else if (nextProps.shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
                state.convertedShifts = shiftCalendarUtil.convertShifts([], nextProps.shiftCalendarMode, nextProps.selectedDate);
            }
            else {
                state.convertedShifts = [];
            }
        }

        const nextSelectedDate = nextProps.selectedDate ? new Date(nextProps.selectedDate) : undefined;
        const prevSelectedDate = prevState.selectedDate ? new Date(prevState.selectedDate) : undefined;

        if (
            (!nextSelectedDate && prevSelectedDate)
            || (nextSelectedDate && !prevSelectedDate)
            || (nextSelectedDate && prevSelectedDate
                && (
                    nextSelectedDate.getFullYear() !== prevSelectedDate.getFullYear()
                    || nextSelectedDate.getMonth() !== prevSelectedDate.getMonth()
                    || nextSelectedDate.getDate() !== prevSelectedDate.getDate()
                ))
        ) {
            state.selectedDate = nextProps.selectedDate;
            if (prevState.shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE) {
                state.convertedShifts = shiftCalendarUtil.convertShifts(prevState.shifts, prevState.shiftCalendarMode, nextSelectedDate);
                shiftCalendarUtil.clearAll(prevState.taskRecurrency, prevState.shiftCalendarMode);
            }
        }

        if (nextProps.shifts && prevState.shifts !== nextProps.shifts) {
            state.shifts = nextProps.shifts;
            state.convertedShifts = shiftCalendarUtil.convertShifts(nextProps.shifts, prevState.shiftCalendarMode, nextProps.selectedDate);
        }

        if (nextProps.taskRecurrency !== prevState.taskRecurrency) {
            state.taskRecurrency = nextProps.taskRecurrency;
        }

        return state;
    }

    public dayBoxClicked(index: number, shiftId: number) {
        const newTaskRecurrency = { ...this.state.taskRecurrency };
        if (!shiftCalendarUtil.removeRecurrence(newTaskRecurrency, index, shiftId, this.state.shiftCalendarMode)) {
            shiftCalendarUtil.addRecurrence(newTaskRecurrency, index, shiftId, this.state.shiftCalendarMode);
        }
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public selectAllDays(index: number, convertedShift: ShiftCalendarItem) {
        const newTaskRecurrency = { ...this.state.taskRecurrency };
        shiftCalendarUtil.selectAllDays(newTaskRecurrency, convertedShift, this.state.shiftCalendarMode);
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public selectAllTimes(dayIndex: number) {
        const newTaskRecurrency = { ...this.state.taskRecurrency };
        shiftCalendarUtil.selectAllTimes(newTaskRecurrency, this.state.convertedShifts, dayIndex, this.state.shiftCalendarMode);
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public dayBoxIsSelected(index: number, shiftId: number): boolean {
        return shiftCalendarUtil.dayBoxIsSelected(this.state.taskRecurrency, index, shiftId, this.state.shiftCalendarMode);
    }

    public selectAll() {
        const newTaskRecurrency = { ...this.state.taskRecurrency };
        shiftCalendarUtil.selectAll(newTaskRecurrency, this.state.convertedShifts, this.state.shiftCalendarMode);
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public clearAll() {
        const newTaskRecurrency = { ...this.state.taskRecurrency };
        shiftCalendarUtil.clearAll(newTaskRecurrency, this.state.shiftCalendarMode);
        this.props.onRecurrencyChanged(newTaskRecurrency);
        this.setState({ taskRecurrency: newTaskRecurrency });
    }

    public shouldShowTimes() {
        return this.state.shiftCalendarMode !== ShiftCalendarMode.DAILY_WEEKLY;
    }

    public render() {
        return (
            <div className={`${styles.shiftCalendar} ${this.props.className}`}>
                <div className={styles.table}>
                    <div className={styles.row}>
                        <div className={`${styles.time} ${this.shouldShowTimes() ? null : styles.hidden}`} />
                        {this.state.days.map((day, i) => {
                            return (
                                <div key={day} className={`${styles.column} ${i === 0 ? styles.first : i === (this.state.days.length - 1) ? styles.last : ""}`}>
                                    <span id={`${this.props.id}SelectAllTimes${i}`} onClick={() => this.selectAllTimes(i)}>{day}</span>
                                </div>
                            );
                        })}
                    </div>

                    {this.state.convertedShifts.map((convertedShift, i) => {
                        return (
                            <div key={convertedShift.time} className={styles.row}>
                                <div className={`${styles.time} ${this.shouldShowTimes() ? null : styles.hidden}`}>
                                    <span id={`${this.props.id}SelectAllDays${i}`} onClick={() => this.selectAllDays(i, convertedShift)}>{convertedShift.time}</span>
                                </div>
                                {convertedShift.days.map((day, index) => {
                                    return (
                                        <div key={`${day}-${index}`} className={`${styles.column} ${index === 0 ? styles.first : index === (convertedShift.days.length - 1) ? styles.last : ""}`}>
                                            {
                                                <div id={`divDayBlock_${day}_${index}`} className={`${styles.dayBlock} ${!day ? styles.invisible : null} ${this.dayBoxIsSelected(index, day) ? styles.selected : null}  ${this.state.shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY ? styles.wide : null}`} onClick={() => this.dayBoxClicked(index, day)}>
                                                    <CheckBoxOutlined className={styles.icon} />
                                                </div>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                {
                    !this.state.convertedShifts.length ? null :
                        < div className={styles.table}>
                            <div className={styles.row}>
                                <div className={`${styles.time} ${styles.invisible} ${this.shouldShowTimes() ? null : styles.hidden}`}>{this.state.convertedShifts[0].time}</div>
                                <div className={`${styles.column} ${styles.first}`}>
                                    <button id={`${this.props.id}SelectAll`} onClick={this.selectAll}><CheckBoxOutlined className={styles.icon} />{i18n.t("shiftCalendar.selectAll")}</button>
                                    <button id={`${this.props.id}ClearAll`} onClick={this.clearAll}><CheckBoxOutlineBlank className={styles.icon} />{i18n.t("shiftCalendar.clearAll")}</button>
                                </div>
                            </div>
                        </div>
                }
            </div >
        );
    }
}

export default connect(mapStateToProps, null)(ShiftCalendar);


// WEBPACK FOOTER //
// ./src/Components/ShiftCalendar/ShiftCalendar.tsx